/**
 * Route Constants
 * Centralized route path definitions for the application
 */

/**
 * Public Route Paths
 */
export const PUBLIC_ROUTES = {
  HOME: '/',
  ABOUT: '/about',
  CONTACT: '/contact',
  SERVICES: '/services',
};

/**
 * Authentication Route Paths
 */
export const AUTH_ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  GOOGLE_CALLBACK: '/auth/google/callback',
  FACEBOOK_CALLBACK: '/auth/facebook/callback',
};

/**
 * Protected Route Paths (Requires Authentication)
 */
export const PROTECTED_ROUTES = {
  DASHBOARD: '/dashboard',
  ADMIN_DASHBOARD: '/admin/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  APPOINTMENTS: '/appointments',
  WORKSHOPS: '/workshops',
  COURSES: '/courses',
  MY_COURSES: '/my-courses',
  LEARN_COURSE: '/my-courses/:courseId/learn',
};

/**
 * API Endpoint Paths
 */
export const API_ROUTES = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
    REFRESH: '/auth/refresh',
    GOOGLE: '/auth/google',
    FACEBOOK: '/auth/facebook',
    CHECK_EMAIL: '/auth/check-email',
    FORGOT_PASSWORD: '/auth/forgot-password',
    VERIFY_RESET_OTP: '/auth/verify-reset-otp',
    RESET_PASSWORD: '/auth/reset-password',
    CREATE_ADMIN: '/auth/admin/create',
    ADMINS: '/auth/admins',
    UPDATE_ADMIN: (id) => `/auth/admins/${id}`,
    DELETE_ADMIN: (id) => `/auth/admins/${id}`,
    VERIFY_EMAIL: '/auth/verify-email',
  },

  // User
  USER: {
    GET_PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile',
    CHANGE_PASSWORD: '/user/change-password',
    DELETE_ACCOUNT: '/user/account',
  },

  // Appointments
  APPOINTMENTS: {
    LIST: '/appointments',
    ADMINS: '/appointments/admins',
    ADMIN_LIST: (adminId) => `/appointments/admin/${adminId}`,
    USER_LIST: (userId) => `/appointments/user/${userId}`,
    CREATE: '/appointments',
    GET: (id) => `/appointments/${id}`,
    UPDATE: (id) => `/appointments/${id}`,
    DELETE: (id) => `/appointments/${id}`,
    RESCHEDULE: (id) => `/appointments/${id}/reschedule`,
    AVAILABILITY: '/appointments/availability',
    UNAVAILABLE: '/appointments/availability/unavailable',
    AVAILABLE: '/appointments/availability/available',
    SLOTS: '/appointments/availability/slots',
  },

  // Workshops
  WORKSHOPS: {
    LIST: '/workshops',
    CREATE: '/workshops',
    GET: (id) => `/workshops/${id}`,
    UPDATE: (id) => `/workshops/${id}`,
    DELETE: (id) => `/workshops/${id}`,
    INTERESTS: '/workshops/interests/list',
    CREATE_INTEREST: '/workshops/interests',
    UPDATE_INTEREST_STATUS: (id) => `/workshops/interests/${id}/status`,
  },

  // Courses
  COURSES: {
    LIST: '/courses',
    CREATE: '/courses',
    GET: (id) => `/courses/${id}`,
    UPDATE: (id) => `/courses/${id}`,
    DELETE: (id) => `/courses/${id}`,
  },

  // Books
  BOOKS: {
    LIST: '/books',
    CREATE: '/books',
    UPLOAD_LOCAL: '/books/upload/local',
    UPDATE: (id) => `/books/${id}`,
    DELETE: (id) => `/books/${id}`,
    INTERESTS: '/books/interests/list',
    CREATE_INTEREST: '/books/interests',
    UPDATE_INTEREST_STATUS: (id) => `/books/interests/${id}/status`,
  },

  // Blogs
  BLOGS: {
    LIST: '/blogs',
    DETAIL: (slugOrId) => `/blogs/${slugOrId}`,
    INTERACTIONS: (slugOrId) => `/blogs/${slugOrId}/interactions`,
    LIKE: (slugOrId) => `/blogs/${slugOrId}/like`,
    COMMENTS: (slugOrId) => `/blogs/${slugOrId}/comments`,
    DELETE_COMMENT: (slugOrId, commentId) => `/blogs/${slugOrId}/comments/${commentId}`,
    CREATE: '/blogs',
    UPDATE: (id) => `/blogs/${id}`,
    DELETE: (id) => `/blogs/${id}`,
  },

  // FAQ Enquiries
  FAQ_ENQUIRIES: {
    LIST: '/faq-enquiries/list',
    CREATE: '/faq-enquiries',
    UPDATE: (id) => `/faq-enquiries/${id}`,
    DELETE: (id) => `/faq-enquiries/${id}`,
  },

  // Contact Enquiries
  CONTACT_ENQUIRIES: {
    LIST: '/contact-enquiries/list',
    CREATE: '/contact-enquiries',
    UPDATE_STATUS: (id) => `/contact-enquiries/${id}/status`,
  },

  // Self-Vastu Reports
  SELF_VASTU: {
    CREATE: '/self-vastu',
    LIST: '/self-vastu/list',
    DETAIL: (id) => `/self-vastu/${id}`,
    DELETE: (id) => `/self-vastu/${id}`,
  },
};

/**
 * External Route Paths
 */
export const EXTERNAL_ROUTES = {
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
  HELP: '/help',
};

/**
 * Error Route Paths
 */
export const ERROR_ROUTES = {
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  SERVER_ERROR: '/500',
};

/**
 * All Routes Combined
 */
export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES,
  ...PROTECTED_ROUTES,
  ...EXTERNAL_ROUTES,
  ...ERROR_ROUTES,
};

export default ROUTES;
